import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Box } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { CollaborativeInboundProgressedMethod } from "analytics/events";
import { useLayoutStore } from "app/stores/useLayoutStore";
import { routes } from "config/routes";
import { useCustomToast } from "shared/hooks/useCustomToast";
import useSwipeScroll from "shared/hooks/useSwipeScroll";
import { OutboundIcon, TrashIcon } from "ui/Icons/Icons";
import { InboundPreDroppingListItemCard } from "ui/InboundPreDroppingListItemCard/InboundPreDroppingListItemCard";
import { SideButton } from "ui/SideButton/SideButton";
import { SwipeScrollWrapper } from "ui/SwipeScrollWrapper";
import { isNullOrUndefined } from "utils/tsHelpers";

import { useInboundUnitStockDataBySku } from "../hooks/useInboundUnitStockDataBySku";
import { useQuantityInputField } from "../hooks/useQuantityInputField";
import { useInboundStore } from "../stores/inboundStore/useInboundStore";
import {
  useDeleteUnclaimedProduct,
  usePreDropQuantityUpdate,
} from "../stores/useInboundPreDropService";

type PreDroppingListPreparationItemProps = {
  sku: string;
  showSwipeButtons: boolean;
  allowBlur: boolean;
  customScrollContainerRef?: React.RefObject<HTMLDivElement | null>;
  defaultTabBarState: boolean;
  allowEditHandlingUnitSize?: boolean;
  withDelay?: boolean;
  isListVerification?: boolean;
  quantityChangeLimit?: number;
  onSubmitQuantityChange?: () => void;
  openEditTotalQuantityModal?: (callback: () => void) => void;
};

export function PreDroppingListPreparationItem({
  sku,
  showSwipeButtons,
  allowBlur,
  customScrollContainerRef,
  defaultTabBarState,
  allowEditHandlingUnitSize = false,
  withDelay = false,
  isListVerification = false,
  quantityChangeLimit,
  onSubmitQuantityChange = () => {},
  openEditTotalQuantityModal = () => {},
}: PreDroppingListPreparationItemProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { showToastUI } = useCustomToast();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRefFromStore = useLayoutStore((state) => state.scrollContainerRef);
  const { scrollContainerRef, handleSwipeScroll, snapWrapperBack } = useSwipeScroll(
    customScrollContainerRef ?? scrollContainerRefFromStore,
    wrapperRef,
  );
  const {
    sharedListId,
    inboundUnitsMap,
    skusWithUnconfirmedQuantities,
    expectedDesadvsSummary,
    inboundUIState,
    confirmSkuQuantity,
    setInboundUnitIdToOutbound,
    setInboundUIState,
  } = useInboundStore(
    useShallow((state) => ({
      sharedListId: state.sharedListId,
      inboundUnitsMap: state.inboundUnitsMap,
      skusWithUnconfirmedQuantities: state.skusWithUnconfirmedQuantities,
      expectedDesadvsSummary: state.expectedDesadvsSummary,
      inboundUIState: state.inboundUIState,
      confirmSkuQuantity: state.confirmSkuQuantity,
      setInboundUnitIdToOutbound: state.setInboundUnitIdToOutbound,
      setInboundUIState: state.setInboundUIState,
    })),
  );

  const {
    skuFocusedForQuantityInput,
    isPartialOutboundAnimationVisible,
    isQuantityEditActive,
    isHUSizeEditActive,
  } = inboundUIState;

  const inboundItemDataProps = inboundUnitsMap[sku];
  const {
    displayQuantity,
    inboundQuantity,
    stockUpdatePlanTotal,
    stockUpdated,
    unitSizeForDisplay,
  } = useInboundUnitStockDataBySku(sku);

  const expectedDesadvs = expectedDesadvsSummary[sku];

  const { deleteUnClaimedProduct, isDeletingUnclaimedProduct } = useDeleteUnclaimedProduct();

  const isQuantityConfirmed = useMemo(
    () => !skusWithUnconfirmedQuantities.includes(sku),
    [sku, skusWithUnconfirmedQuantities],
  );

  const confirmQuantity = useCallback(() => {
    confirmSkuQuantity(sku);
  }, [confirmSkuQuantity, sku]);

  const isQuantityMatchingExpectation = useMemo(() => {
    if (!isQuantityConfirmed || !expectedDesadvs) return null;
    if (expectedDesadvs.unitType === "single") {
      return expectedDesadvs.totalQuantity === inboundQuantity;
    }
    return (
      expectedDesadvs.totalQuantity === inboundQuantity ||
      expectedDesadvs.unitSizeForDisplay === displayQuantity
    );
  }, [isQuantityConfirmed, expectedDesadvs, inboundQuantity, displayQuantity]);

  const isTotalQuantityMatchingExpectation = useMemo(() => {
    if (!isQuantityConfirmed || !expectedDesadvs || isHUSizeEditActive) return true;

    return unitSizeForDisplay === expectedDesadvs.handlingUnitSize;
  }, [isQuantityConfirmed, expectedDesadvs, isHUSizeEditActive, unitSizeForDisplay]);

  const displayQuantityInContext = useMemo(() => {
    return isHUSizeEditActive ? 1 : displayQuantity;
  }, [displayQuantity, isHUSizeEditActive]);

  const displayTotalQuantityInContext = useMemo(() => {
    return isHUSizeEditActive ? unitSizeForDisplay : stockUpdatePlanTotal;
  }, [isHUSizeEditActive, stockUpdatePlanTotal, unitSizeForDisplay]);

  const elementRef = useRef<HTMLDivElement | undefined>(undefined);

  const { updatePreDropQuantity, editPreDropQuantityError, isHUUpdate } =
    usePreDropQuantityUpdate();
  const notifyLimitExceeded = useCallback(() => {
    showToastUI?.({
      id: "quantity-Limit-eceeded",
      status: "info",
      title: "quantity-Limit-eceeded-title",
      description: intl.formatMessage(
        { id: "quantity-Limit-eceeded-description" },
        { quantityChangeLimit },
      ),
    });
  }, [intl, quantityChangeLimit, showToastUI]);
  const lastQuantityWasValidRef = useRef(true);
  const setQuantity = useCallback(
    ({ oldValue, newValue }: { oldValue: number; newValue: number }) => {
      lastQuantityWasValidRef.current = true;

      if (isNullOrUndefined(sharedListId)) {
        return oldValue;
      }
      const isHandlingUnit = unitSizeForDisplay > 1;
      if (quantityChangeLimit !== undefined) {
        const count = isHandlingUnit ? newValue * unitSizeForDisplay : newValue;
        const isOverLimit = Math.abs(count) > quantityChangeLimit;
        if (isOverLimit) {
          lastQuantityWasValidRef.current = false;
          notifyLimitExceeded();
          return oldValue;
        }
      }
      if (oldValue !== newValue) {
        updatePreDropQuantity({
          listId: sharedListId,
          sku,
          oldValue,
          newValue,
          method: CollaborativeInboundProgressedMethod.ManualSU,
          isHandlingUnit,
        });
      }
      return newValue;
    },
    [
      notifyLimitExceeded,
      quantityChangeLimit,
      sharedListId,
      sku,
      unitSizeForDisplay,
      updatePreDropQuantity,
    ],
  );
  const setHUSize = useCallback(
    ({ oldValue, newValue }: { oldValue: number; newValue: number }) => {
      if (newValue < 1 || isNullOrUndefined(sharedListId)) {
        return oldValue;
      }
      if (quantityChangeLimit !== undefined) {
        const count = newValue * displayQuantity;
        const isOverLimit = Math.abs(count) > quantityChangeLimit;
        if (isOverLimit) {
          notifyLimitExceeded();
          return oldValue;
        }
      }
      if (oldValue !== newValue) {
        updatePreDropQuantity({
          listId: sharedListId,
          sku,
          oldValue,
          newValue,
          method: CollaborativeInboundProgressedMethod.ManualHU,
          isHandlingUnit: unitSizeForDisplay > 1,
        });
      }
      return newValue;
    },
    [
      displayQuantity,
      notifyLimitExceeded,
      quantityChangeLimit,
      sharedListId,
      sku,
      unitSizeForDisplay,
      updatePreDropQuantity,
    ],
  );

  const onClickRemoveProduct = useCallback(() => {
    if (isDeletingUnclaimedProduct || !sharedListId) return;
    deleteUnClaimedProduct({
      listId: sharedListId,
      sku,
      quantity: displayTotalQuantityInContext,
      isHandlingUnit: unitSizeForDisplay > 1,
    });
    snapWrapperBack();
  }, [
    deleteUnClaimedProduct,
    displayTotalQuantityInContext,
    isDeletingUnclaimedProduct,
    sharedListId,
    sku,
    snapWrapperBack,
    unitSizeForDisplay,
  ]);

  const onClickOutboundButton = useCallback(() => {
    setInboundUnitIdToOutbound(sku);
    navigate(routes.inbound.outboundUnclaimed);
  }, [navigate, setInboundUnitIdToOutbound, sku]);

  const handleQuantitySubmit = useCallback(() => {
    if (lastQuantityWasValidRef.current) {
      onSubmitQuantityChange?.();
    }
  }, [onSubmitQuantityChange]);

  const {
    fieldProps: quantityFieldProps,
    focus: focusQuantityInput,
    blur: blurQuantityInput,
    resetInputValue,
  } = useQuantityInputField({
    cardRef: elementRef,
    scrollContainerRef,
    defaultTabBarState,
    allowBlur,
    setValue: setQuantity,
    value: displayQuantity,
    onSubmit: handleQuantitySubmit,
    applyValidationTransform: (rawValue: string) => {
      return rawValue === "" ? "1" : rawValue;
    },
  });

  const {
    fieldProps: totalQuantityFieldProps,
    focus: focusTotalQuantityInput,
    resetInputValue: resetTotalInputValue,
  } = useQuantityInputField({
    cardRef: elementRef,
    scrollContainerRef,
    defaultTabBarState,
    allowBlur,
    setValue: setHUSize,
    value: displayTotalQuantityInContext,
    uiEffects: {
      effect: () => {
        setInboundUIState({ isHUSizeEditActive: true });
        snapWrapperBack();
      },
      cleanup: () => {
        setInboundUIState({ isHUSizeEditActive: false });
      },
    },
    applyValidationTransform: (rawValue: string) => {
      return rawValue === "" || parseInt(rawValue, 10) < 1 ? null : rawValue;
    },
  });

  const showTotalQuantityHint = useMemo(
    () => !allowBlur && unitSizeForDisplay > 1 && isQuantityEditActive,
    [allowBlur, unitSizeForDisplay, isQuantityEditActive],
  );

  const displayedTotalQuantity = useMemo(() => {
    if (showTotalQuantityHint) {
      const quantity = Number(quantityFieldProps?.inputProps?.value || displayQuantity);
      return quantity * unitSizeForDisplay;
    }
    return displayTotalQuantityInContext;
  }, [
    showTotalQuantityHint,
    quantityFieldProps?.inputProps?.value,
    displayQuantity,
    unitSizeForDisplay,
    displayTotalQuantityInContext,
  ]);

  useEffect(() => {
    if (editPreDropQuantityError) {
      if (isHUUpdate) {
        resetTotalInputValue();
      } else {
        resetInputValue();
      }
    }
  }, [editPreDropQuantityError, isHUUpdate, resetInputValue, resetTotalInputValue]);

  const startEditingTotalQuantity = useCallback(() => {
    setTimeout(() => focusTotalQuantityInput({ preventScroll: true }));
  }, [focusTotalQuantityInput]);

  const onClickTotalQuantityNumber = useCallback(() => {
    if (!allowEditHandlingUnitSize || stockUpdated) return;
    openEditTotalQuantityModal(startEditingTotalQuantity);
  }, [
    allowEditHandlingUnitSize,
    openEditTotalQuantityModal,
    startEditingTotalQuantity,
    stockUpdated,
  ]);

  useEffect(() => {
    if (!lastQuantityWasValidRef.current) {
      focusQuantityInput({ preventScroll: true });
      lastQuantityWasValidRef.current = true;
    }
  }, [focusQuantityInput, displayQuantity]);

  useEffect(() => {
    if (expectedDesadvs?.totalQuantity === displayQuantity) return;

    if (skuFocusedForQuantityInput === sku) {
      const focusAction = () => {
        focusQuantityInput({ preventScroll: true });
        setInboundUIState({ skuFocusedForQuantityInput: undefined });
      };
      setTimeout(focusAction, withDelay ? 10 : 0);
    } else if (skuFocusedForQuantityInput === null) {
      blurQuantityInput();
      setInboundUIState({ skuFocusedForQuantityInput: undefined });
    }
  }, [
    blurQuantityInput,
    focusQuantityInput,
    setInboundUIState,
    sku,
    skuFocusedForQuantityInput,
    withDelay,
    displayQuantity,
    expectedDesadvs,
  ]);

  const isPartiallyOutbounded = useMemo(
    () => stockUpdatePlanTotal < (inboundQuantity ?? 0),
    [stockUpdatePlanTotal, inboundQuantity],
  );

  const [showPartiallyOutboundedAnim] = useState(() => {
    return isPartialOutboundAnimationVisible === sku;
  });

  useEffect(() => {
    if (showPartiallyOutboundedAnim) {
      elementRef.current?.scrollIntoView();
      setInboundUIState({ isPartialOutboundAnimationVisible: null });
    }
  }, [showPartiallyOutboundedAnim, setInboundUIState]);

  const mainComponent = (
    <InboundPreDroppingListItemCard
      elementRef={(element) => {
        elementRef.current = element;
      }}
      stockUpdatePlanTotal={displayedTotalQuantity}
      translatedInboundUnit={inboundItemDataProps}
      quantityFieldProps={quantityFieldProps}
      displayQuantity={displayQuantityInContext}
      displayAsHandlingUnit={unitSizeForDisplay > 1}
      isPartiallyOutbounded={isPartiallyOutbounded}
      showPartiallyOutboundedAnimation={showPartiallyOutboundedAnim}
      isEditingQuantity={isQuantityEditActive}
      isEditingTotal={isHUSizeEditActive}
      onClickTotalQuantityNumber={onClickTotalQuantityNumber}
      totalQuantityFieldProps={totalQuantityFieldProps}
      onConfirmQuantity={confirmQuantity}
      isMatchedtDesadvItem={!isListVerification && !!expectedDesadvs}
      expectedUnitType={expectedDesadvs?.unitType}
      expectedNumberOfUnits={expectedDesadvs?.unitSizeForDisplay}
      isQuantityMatchingExpectation={isQuantityMatchingExpectation}
      isTotalQuantityMatchingExpectation={isTotalQuantityMatchingExpectation}
    />
  );

  const actionSideButtons = (
    <Box flex="0 0 137px">
      <SideButton
        color="red.500"
        bgColor="red.100"
        icon={<TrashIcon boxSize={6} />}
        label={intl.formatMessage({
          id: "components.inbound.pre-dropping-list-item.delete-label",
          defaultMessage: "Delete",
        })}
        onClick={onClickRemoveProduct}
        data-testid="remove-product"
      />
      <SideButton
        color="purple.500"
        bgColor="purple.100"
        icon={<OutboundIcon boxSize={6} />}
        label={intl.formatMessage({
          id: "components.inbound.pre-dropping-list-item.outbound-label",
          defaultMessage: "Outbound",
        })}
        onClick={onClickOutboundButton}
        data-testid="go-to-inbound-outbound-page"
      />
    </Box>
  );

  return (
    <SwipeScrollWrapper
      mainComponent={mainComponent}
      handleSwipeScroll={handleSwipeScroll}
      actionsRight={stockUpdated && showSwipeButtons ? undefined : actionSideButtons}
      showActionsRight={showSwipeButtons}
      ref={wrapperRef}
    />
  );
}
